import { useMemo } from 'react';
import { useSalesforceData } from './useSalesforceData';
import { soqlDateFilter, TimePeriod } from '../utils/timePeriods';

/** One GROUP BY OwnerId, Type row from the aggregate Task query. */
interface ActivityAggRow {
  OwnerId: string;
  Type: string | null;
  total: number;
}

export interface AssigneeActivity {
  ownerId: string;
  calls: number;
  emails: number;
  tasks: number;
  total: number;
}

/**
 * Completed-activity counts (Calls / Emails / Tasks) per assignee.
 *
 * Uses a SOQL GROUP BY OwnerId, Type aggregate so the server does the counting
 * and only ~(reps × types) rows come back — never the raw Task rows. The query
 * is always owner-scoped and date-windowed and capped, which is what the old
 * hook lacked (it dropped the owner filter with no rep selected, had no LIMIT,
 * and let `all_time` reach back to the year 2000).
 *
 * @param ownerIds  Sales-rep user Ids to include (from useSalesforceUsers).
 * @param aeId      When set, narrows to a single rep; otherwise all ownerIds.
 * @param period    Time window applied to Task.ActivityDate.
 */
export function useActivityMetrics(
  ownerIds: string[],
  aeId: string | null,
  period: TimePeriod
) {
  const soql = useMemo(() => {
    const ids = aeId ? [aeId] : ownerIds;
    if (ids.length === 0) return null;
    const idList = ids.map((id) => `'${id}'`).join(',');
    return `SELECT OwnerId, Type, COUNT(Id) total FROM Task WHERE Status = 'Completed' AND ${soqlDateFilter(
      'ActivityDate',
      period
    )} AND OwnerId IN (${idList}) GROUP BY OwnerId, Type LIMIT 2000`;
  }, [ownerIds, aeId, period]);

  const { data, loading, error, refetch } = useSalesforceData<ActivityAggRow>(soql);

  const byAssignee = useMemo(() => {
    const map = new Map<string, AssigneeActivity>();
    for (const row of data) {
      const entry =
        map.get(row.OwnerId) ??
        { ownerId: row.OwnerId, calls: 0, emails: 0, tasks: 0, total: 0 };
      const n = Number(row.total) || 0;
      if (row.Type === 'Call') entry.calls += n;
      else if (row.Type === 'Email') entry.emails += n;
      else entry.tasks += n; // generic Tasks (any other / null Type)
      entry.total += n;
      map.set(row.OwnerId, entry);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [data]);

  return { byAssignee, loading, error, refetch };
}
